(function () {
  // Hotspots sit on the inside face; orbit the camera 180° so that side faces forward
  // on load and when zooming to each feature (without rotating the mesh).
  const CAMERA_THETA_OFFSET = 180;
  const DEFAULT_CAMERA_ORBIT = '180deg 75deg 0.33m';

  function getStickCameraConfig(root) {
    if (!root) return null;
    const orbit = root.dataset.cameraOrbit;
    const target = root.dataset.cameraTarget;
    if (!orbit && !target) return null;
    return {
      orbit: orbit || '',
      target: target || '',
    };
  }

  // Fixed framing: 40% closer than the default ~0.55m fit-model floor.
  // Lock min-camera-orbit radius here so users cannot zoom in past this framing.
  const CLOSEST_RADIUS = '0.33m';

  function lockMaxZoomToRadius(modelViewer, radius) {
    if (!modelViewer || !radius) return;
    modelViewer.setAttribute('min-camera-orbit', `auto auto ${radius}`);
  }

  function resolveClosestRadius(modelViewer) {
    lockMaxZoomToRadius(modelViewer, CLOSEST_RADIUS);
    return CLOSEST_RADIUS;
  }

  function applyConfiguredCamera(modelViewer, config) {
    if (!config) return false;
    if (config.target) {
      modelViewer.cameraTarget = config.target;
    }
    if (config.orbit) {
      const parts = config.orbit.trim().split(/\s+/);
      if (parts.length >= 2) {
        // Stick-specific framing (ADB): snap to closest framing radius on apply
        const radius = resolveClosestRadius(modelViewer);
        modelViewer.cameraOrbit = `${parts[0]} ${parts[1]} ${radius}`;
        if (typeof modelViewer.jumpCameraToGoal === 'function') {
          modelViewer.jumpCameraToGoal();
        }
      } else {
        modelViewer.cameraOrbit = config.orbit;
      }
    }
    return true;
  }

  function applyDefaultCamera(modelViewer, root) {
    const config = getStickCameraConfig(root);
    if (applyConfiguredCamera(modelViewer, config)) return;

    const parts = DEFAULT_CAMERA_ORBIT.trim().split(/\s+/);
    const radius = resolveClosestRadius(modelViewer);
    modelViewer.cameraOrbit = `${parts[0]} ${parts[1]} ${radius}`;
    if (typeof modelViewer.jumpCameraToGoal === 'function') {
      modelViewer.jumpCameraToGoal();
    }
  }

  function normalizeId(id) {
    return String(id);
  }

  function normalizeText(text) {
    return String(text).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  }

  function getTitleWords(title) {
    return normalizeText(title)
      .split(/\s+/)
      .filter((word) => word.length > 1 && word !== 'y1');
  }

  function findMetafieldDescription(title, stickFeatures) {
    if (!stickFeatures || !stickFeatures.length) return '';

    const titleWords = getTitleWords(title);
    if (!titleWords.length) return '';

    for (const entry of stickFeatures) {
      const normalized = normalizeText(entry);
      // Match whole words or compound forms like "RecurvedToe" / "SlingGroove"
      const allMatch = titleWords.every((word) => normalized.includes(word));
      if (allMatch) return entry;
    }

    return '';
  }

  function normalizeList(value) {
    if (!value) return [];
    if (Array.isArray(value)) {
      return value.map((item) => String(item).trim()).filter(Boolean);
    }
    if (typeof value === 'string') {
      return value
        .split(/\r?\n|,/)
        .map((item) => item.trim())
        .filter(Boolean);
    }
    return [];
  }

  function findClosestLabel(featureTitle, labels) {
    if (!labels.length) return '';

    const featureWords = getTitleWords(featureTitle);
    if (!featureWords.length) return '';

    let best = '';
    let bestScore = 0;

    labels.forEach((label) => {
      const labelWords = getTitleWords(label);
      if (!labelWords.length) return;

      const matches = featureWords.filter((word) =>
        labelWords.some((labelWord) => labelWord.includes(word) || word.includes(labelWord))
      ).length;

      if (matches > bestScore) {
        bestScore = matches;
        best = label;
      }
    });

    return bestScore > 0 ? best : '';
  }

  function normalizeTimes(value) {
    if (!value) return [];
    if (Array.isArray(value)) {
      return value.map((item) => {
        const n = parseInt(item, 10);
        return Number.isFinite(n) ? n : null;
      });
    }
    if (typeof value === 'string') {
      return value
        .split(/\r?\n|,/)
        .map((item) => {
          const n = parseInt(item.trim(), 10);
          return Number.isFinite(n) ? n : null;
        });
    }
    return [];
  }

  function enrichFeaturesFromMetafields(features, featureButtons, stickFeatures, videoFeatureTimes) {
    const buttons = normalizeList(featureButtons);
    const descriptions = normalizeList(stickFeatures);
    const times = normalizeTimes(videoFeatureTimes);

    if (!buttons.length && !descriptions.length && !times.length) return features;

    return features.map((feature) => {
      const next = Object.assign({}, feature);
      // Match by feature name, never by index — index order often differs from hotspot geometry order
      const buttonLabel = findClosestLabel(feature.title, buttons);
      if (buttonLabel) {
        next.title = buttonLabel;
        const buttonIndex = buttons.indexOf(buttonLabel);
        if (buttonIndex >= 0 && times[buttonIndex] != null) {
          next.videoTime = times[buttonIndex];
        }
      }

      const matchedDescription =
        findMetafieldDescription(next.title, descriptions) ||
        findMetafieldDescription(feature.title, descriptions);
      if (matchedDescription) next.description = matchedDescription;

      return next;
    });
  }

  function seekBackgroundVideo(root, feature) {
    if (feature.videoTime == null) return;
    const video = root.querySelector('.stick-hotspots__video');
    if (!video) return;

    const applySeek = () => {
      video.currentTime = feature.videoTime;
      if (video.paused && typeof video.play === 'function') {
        video.play().catch(() => {});
      }
    };

    if (video.readyState >= 1) {
      applySeek();
    } else {
      video.addEventListener('loadedmetadata', applySeek, { once: true });
    }
  }

  function offsetOrbitTheta(theta, offsetDeg) {
    const match = String(theta).match(/^(-?\d+(?:\.\d+)?)(deg|rad)$/i);
    if (!match) return theta;

    const next = ((parseFloat(match[1]) + offsetDeg) % 360 + 360) % 360;
    return `${next}${match[2]}`;
  }

  function applyHotspotCamera(modelViewer, feature, root) {
    const target = feature.cameraTarget || feature.position;
    if (target) {
      modelViewer.cameraTarget = target;
    }

    const stickConfig = getStickCameraConfig(root);
    let theta = offsetOrbitTheta('0deg', CAMERA_THETA_OFFSET);
    let phi = '75deg';
    const orbitSource =
      feature.cameraOrbit ||
      (stickConfig && stickConfig.orbit) ||
      DEFAULT_CAMERA_ORBIT;

    if (orbitSource) {
      const parts = orbitSource.trim().split(/\s+/);
      if (parts.length >= 2) {
        // Stick-level framing (ADB) uses absolute yaw/pitch — no 180° offset
        if (stickConfig && stickConfig.orbit) {
          theta = parts[0];
        } else {
          theta = offsetOrbitTheta(parts[0], CAMERA_THETA_OFFSET);
        }
        phi = parts[1];
      }
    }

    // Closest framing radius for all stick models (same as ADB)
    const radius = resolveClosestRadius(modelViewer);

    const nextOrbit = `${theta} ${phi} ${radius}`;
    modelViewer.cameraOrbit = nextOrbit;
    if (typeof modelViewer.jumpCameraToGoal === 'function') {
      modelViewer.jumpCameraToGoal();
    }
    modelViewer.setAttribute('auto-rotate', '');
  }

  function selectFeature(root, features, featureId, modelViewer) {
    const normalizedId = normalizeId(featureId);
    const feature = features.find((item) => normalizeId(item.id) === normalizedId);
    if (!feature) return;

    root.querySelectorAll('[data-feature-trigger]').forEach((button) => {
      const isActive = normalizeId(button.dataset.featureTrigger) === normalizedId;
      button.classList.toggle('button--primary', isActive);
      button.classList.toggle('button--secondary', !isActive);
      button.setAttribute('aria-current', isActive ? 'true' : 'false');
      button.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    root.querySelectorAll('.stick-hotspot').forEach((hotspot) => {
      hotspot.classList.toggle('is-active', normalizeId(hotspot.dataset.featureId) === normalizedId);
    });

    const description = root.querySelector('[data-feature-description]');

    if (description) description.textContent = feature.description;

    seekBackgroundVideo(root, feature);

    if (modelViewer && !root.classList.contains('stick-hotspots--fallback')) {
      applyHotspotCamera(modelViewer, feature, root);
    }
  }

  function getPositionedFeatures(features) {
    return features.filter((feature) => feature.position);
  }

  function bindHotspotEvents(root, features, positionedFeatures, modelViewer) {
    positionedFeatures.forEach((feature) => {
      modelViewer.addEventListener(`hotspot-${feature.slot}-click`, () => {
        selectFeature(root, features, feature.id, modelViewer);
      });
    });

    root.querySelectorAll('.stick-hotspot').forEach((hotspot) => {
      hotspot.addEventListener('click', (event) => {
        event.stopPropagation();
        selectFeature(root, features, hotspot.dataset.featureId, modelViewer);
      });
    });
  }

  function createNav(root, features) {
    const nav = root.querySelector('[data-hotspots-nav]');
    if (!nav) return;

    const panelId = nav.dataset.panelId || '';
    nav.innerHTML = '';

    features.forEach((feature, index) => {
      const item = document.createElement('li');
      item.className = 'stick-hotspots__nav-item';

      const button = document.createElement('button', { is: 'hover-button' });
      button.type = 'button';
      const isFirst = index === 0;
      button.className = `button button--md button--fixed pointer-events-auto ${isFirst ? 'button--primary' : 'button--secondary'}`;
      button.dataset.featureTrigger = normalizeId(feature.id);
      button.setAttribute('role', 'tab');
      if (panelId) button.setAttribute('aria-controls', panelId);

      button.setAttribute('aria-selected', isFirst ? 'true' : 'false');
      if (isFirst) button.setAttribute('aria-current', 'true');

      const fill = document.createElement('span');
      fill.className = 'btn-fill';
      fill.setAttribute('data-fill', '');

      const text = document.createElement('span');
      text.className = 'btn-text';
      text.textContent = feature.title;

      button.append(fill, text);
      item.appendChild(button);
      nav.appendChild(item);
    });
  }

  function seedDetail(root, feature) {
    if (!feature) return;

    const description = root.querySelector('[data-feature-description]');

    if (description) description.textContent = feature.description || '';
  }

  function createHotspots(modelViewer, positionedFeatures) {
    positionedFeatures.forEach((feature, index) => {
      const parsedSlot = parseInt(feature.slot, 10);
      const slot = Number.isFinite(parsedSlot) ? parsedSlot : index + 1;
      feature.slot = slot;

      const hotspot = document.createElement('button');
      hotspot.type = 'button';
      hotspot.className = 'stick-hotspot';
      hotspot.slot = `hotspot-${slot}`;
      hotspot.dataset.featureId = normalizeId(feature.id);
      hotspot.setAttribute('data-position', feature.position);
      hotspot.setAttribute('data-normal', feature.normal || '0m 1m 0m');
      hotspot.setAttribute('data-visibility-attribute', 'visible');
      hotspot.setAttribute('aria-label', feature.title);
      modelViewer.appendChild(hotspot);
    });
  }

  function supportsModelViewer() {
    if (!window.customElements) return false;
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
      if (!gl) return false;
    } catch (error) {
      return false;
    }
    return true;
  }

  function waitForModelViewerDefined(timeoutMs) {
    if (window.customElements && customElements.get('model-viewer')) {
      return Promise.resolve(true);
    }
    if (!window.customElements || typeof customElements.whenDefined !== 'function') {
      return Promise.resolve(false);
    }

    return new Promise((resolve) => {
      let settled = false;
      const finish = (defined) => {
        if (settled) return;
        settled = true;
        resolve(defined);
      };

      const timer = window.setTimeout(() => finish(false), timeoutMs);
      customElements.whenDefined('model-viewer').then(() => {
        window.clearTimeout(timer);
        finish(true);
      });
    });
  }

  function enableFallbackMode(root) {
    root.classList.add('stick-hotspots--fallback');
  }

  function whenModelReady(modelViewer, callback) {
    let done = false;

    const finish = () => {
      if (done) return;
      done = true;
      callback();
    };

    if (modelViewer.loaded) {
      finish();
      return;
    }

    modelViewer.addEventListener('load', finish, { once: true });

    // poster-dismissed only if load never fired but viewer is already loaded
    modelViewer.addEventListener(
      'poster-dismissed',
      () => {
        if (modelViewer.loaded) finish();
      },
      { once: true }
    );
  }

  function applyInitialClosestCamera(modelViewer, root, features) {
    applyDefaultCamera(modelViewer, root);
    selectFeature(root, features, features[0].id, modelViewer);
  }

  function bindFeatureNav(root, features) {
    root.querySelectorAll('[data-feature-trigger]').forEach((button) => {
      button.addEventListener('click', () => {
        selectFeature(root, features, button.dataset.featureTrigger, root._stickModelViewer || null);
      });
    });
  }

  function initInteractiveHotspots(root, features, modelViewer) {
    root._stickModelViewer = modelViewer;
    root.classList.remove('stick-hotspots--fallback');
    applyDefaultCamera(modelViewer, root);

    const positionedFeatures = getPositionedFeatures(features);
    createHotspots(modelViewer, positionedFeatures);

    if (positionedFeatures.length) {
      bindHotspotEvents(root, features, positionedFeatures, modelViewer);
    }

    modelViewer.addEventListener(
      'error',
      () => {
        root._stickModelViewer = null;
        enableFallbackMode(root);
      },
      { once: true }
    );

    whenModelReady(modelViewer, () => {
      if (root.classList.contains('stick-hotspots--fallback')) return;
      modelViewer.setAttribute('camera-controls', '');
      modelViewer.setAttribute('auto-rotate', '');
      modelViewer.setAttribute('interpolation-decay', '75');
      applyInitialClosestCamera(modelViewer, root, features);
    });
  }

  function initStickHotspots(root) {
    if (root.dataset.hotspotsInitialized === 'true') return;

    const configElement = root.querySelector('[data-hotspots-config]');
    if (!configElement) return;

    const stickFeaturesElement = root.querySelector('[data-stick-features]');
    const stickFeatures = stickFeaturesElement
      ? JSON.parse(stickFeaturesElement.textContent)
      : [];

    const featureButtonsElement = root.querySelector('[data-stick-feature-buttons]');
    const featureButtons = featureButtonsElement
      ? JSON.parse(featureButtonsElement.textContent)
      : [];

    const videoTimesElement = root.querySelector('[data-video-feature-times]');
    const videoFeatureTimes = videoTimesElement
      ? JSON.parse(videoTimesElement.textContent)
      : [];

    const features = enrichFeaturesFromMetafields(
      JSON.parse(configElement.textContent),
      featureButtons,
      stickFeatures,
      videoFeatureTimes
    );
    if (!features.length) return;

    const modelViewer = root.querySelector('model-viewer');
    root.dataset.hotspotsInitialized = 'true';
    root._stickModelViewer = null;

    // Nav + copy always available; camera/hotspots attach only when model-viewer works
    createNav(root, features);
    seedDetail(root, features[0]);
    bindFeatureNav(root, features);
    selectFeature(root, features, features[0].id, null);

    if (!supportsModelViewer() || !modelViewer) {
      enableFallbackMode(root);
      return;
    }

    waitForModelViewerDefined(4000).then((defined) => {
      if (!defined || !customElements.get('model-viewer')) {
        enableFallbackMode(root);
        return;
      }
      initInteractiveHotspots(root, features, modelViewer);
    });
  }

  function boot() {
    document.querySelectorAll('[data-stick-hotspots]').forEach(initStickHotspots);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-stick-hotspots]').forEach((root) => {
      root.dataset.hotspotsInitialized = 'false';
      initStickHotspots(root);
    });
  });
})();
